import {gql} from '@apollo/client';
import {asyncNoop} from '@benchling/js-utils';
import {createStyles, ScrollArea, Stack, Text, TextInput} from '@mantine/core';
import React, {useMemo, useState} from 'react';

import {ProcedureType, StudyType} from 'graphql-client/__generated/graphqlGlobalTypes';
import {QueryResultType, useBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {getFragmentWrapper} from 'graphql-client/declarative-translations/external/getFragmentWrapper';
import getProcedureUIString, {ProceduresStringKeys, StringCase} from 'procedures/ProceduresStringKeys';
import getTestProps from 'test/lib/cypress/getTestProps';
import {
  NODE_HOVER_BOX_SHADOW,
  STACKED_NODE_HOVER_BOX_SHADOW,
} from 'ui-library/reactflow/nodes/BenchlingNode/BenchlingNodeDesignConstants';
import {CenteredSpinner} from 'ui-library/Spinner';

import {
  ProcedureSearchSidepanelListingQuery,
  ProcedureSearchSidepanelListingQuery_proceduresListing_ProcedureNode as ProcedureSearchSidepanelProcedureNode,
} from './__generated/ProcedureSearchSidepanelListingQuery';
import {PresentationalProcedureCard, ProcedureCardClasses, useProcedureCardStyles} from './ProcedureCard';
import {shouldShowProcedureForProcedureRunCreation} from './ProcedureDropdown';

export const PROCEDURE_SEARCH_SIDEPANEL_LISTING_QUERY = gql`
  query ProcedureSearchSidepanelListingQuery($procedureTypes: [ProcedureType!])
  @skipAudit
  @engComponent(name: STUDIES) {
    proceduresListing(procedureTypes: $procedureTypes, excludeArchived: true) {
      id
      name
      editUrl
      publishedProcedureFlowchartConfigVersion {
        id
        nodes {
          id
        }
      }
      templateCollection {
        id
        name
      }
      currentPublishingRecord {
        ... on PublishingRecordNode {
          id
          publishingStatus
        }
        ...InaccessibleInfoGraphQL
      }
    }
  }
  ${getFragmentWrapper('InaccessibleInfo').fragment}
`;

export interface SelectedProcedure {
  procedureId: string;
  workflowFlowchartConfigVersionId: string;
}

export const PROCEDURE_SELECTION_DND_MIME_TYPE = 'application/x-benchling-selected-procedure';

// Transparent padding around the drag preview so setDragImage does not clip the card's box-shadow.
// Shared between the wrapper's padding and the setDragImage offset math in ProcedureSearchSidepanel.
const DRAG_PREVIEW_STACK_PADDING_PX = 6;

interface ProcedureSearchSidepanelProps {
  procedureType: ProcedureType;
  studyType: StudyType;
}

interface UseFilteredProceduresProps {
  procedures: Array<ProcedureSearchSidepanelProcedureNode>;
  searchQuery: string;
}

type ProcedureNodeWithPublishedPFCVP = ProcedureSearchSidepanelProcedureNode & {
  publishedProcedureFlowchartConfigVersion: NonNullable<
    ProcedureSearchSidepanelProcedureNode['publishedProcedureFlowchartConfigVersion']
  >;
};

function isEligibleProcedureForProcedureRunCreation(
  procedure: ProcedureSearchSidepanelProcedureNode
): procedure is ProcedureNodeWithPublishedPFCVP {
  return (
    shouldShowProcedureForProcedureRunCreation(procedure) &&
    procedure.publishedProcedureFlowchartConfigVersion != null
  );
}

function useFilteredProcedures({
  procedures,
  searchQuery,
}: UseFilteredProceduresProps): Array<ProcedureNodeWithPublishedPFCVP> {
  // Filter out unpublished procedures (when publishing is enabled) and legacy (non-PoW) procedures
  const eligibleProcedures = useMemo(
    () =>
      procedures
        .filter((procedure): procedure is ProcedureNodeWithPublishedPFCVP =>
          isEligibleProcedureForProcedureRunCreation(procedure)
        )
        .toSorted(
          (a, b) =>
            a.templateCollection.name.localeCompare(b.templateCollection.name) || a.name.localeCompare(b.name)
        ),
    [procedures]
  );

  // Filter based on search query
  return useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLocaleLowerCase();
    if (normalizedQuery.length === 0) {
      return eligibleProcedures;
    }

    return eligibleProcedures.filter((procedure) =>
      procedure.name.toLocaleLowerCase().includes(normalizedQuery)
    );
  }, [eligibleProcedures, searchQuery]);
}

// Builds a lightweight, purpose-made preview element that mirrors the focused ProcedureCard
// appearance without deep-cloning the live card DOM (which caused drag lag). Styling is reused from
// ProcedureCard's createStyles classes so the design values live in a single place.
function buildDragPreviewCard(
  dragSourceElement: HTMLElement,
  procedureName: string,
  templateCollectionName: string,
  hasMultipleUnitOps: boolean,
  classes: ProcedureSearchSidepanelClasses,
  procedureCardClasses: ProcedureCardClasses
): HTMLDivElement {
  const sourceBounds = dragSourceElement.getBoundingClientRect();

  // Create an outer div of the same dimensions as the source element
  const card = document.createElement('div');
  card.className = hasMultipleUnitOps
    ? `${classes.dragPreviewCard} ${classes.dragPreviewCardStacked}`
    : classes.dragPreviewCard;
  card.style.width = `${sourceBounds.width}px`;
  card.style.height = `${sourceBounds.height}px`;

  const content = document.createElement('div');
  content.className = classes.dragPreviewContent;

  const header = document.createElement('div');
  header.className = classes.dragPreviewHeader;

  // Cloning the single icon node keeps it visually faithful without deep-cloning the card.
  const sourceIcon = dragSourceElement.querySelector('[data-test-component="BenchlingIcon"]');
  if (sourceIcon != null) {
    header.appendChild(sourceIcon.cloneNode(true));
  }

  const name = document.createElement('span');
  name.textContent = procedureName;
  name.className = procedureCardClasses.procedureName;
  header.appendChild(name);

  const collection = document.createElement('span');
  collection.textContent = templateCollectionName;
  collection.className = procedureCardClasses.collectionName;

  content.appendChild(header);
  content.appendChild(collection);
  card.appendChild(content);
  return card;
}

function handleProcedureCardDragStart(
  event: React.DragEvent<HTMLDivElement>,
  procedure: ProcedureNodeWithPublishedPFCVP,
  hasMultipleUnitOps: boolean,
  classes: ProcedureSearchSidepanelClasses,
  procedureCardClasses: ProcedureCardClasses
): void {
  const selectedProcedure: SelectedProcedure = {
    procedureId: procedure.id,
    workflowFlowchartConfigVersionId: procedure.publishedProcedureFlowchartConfigVersion.id,
  };
  const payload = JSON.stringify(selectedProcedure);
  event.dataTransfer.effectAllowed = 'copy';
  event.dataTransfer.setData(PROCEDURE_SELECTION_DND_MIME_TYPE, payload);

  // Build a drag preview that looks like the card without cloning the full card (which would introduce lag)
  // We can't use the browser default preview because the box shadow creating the stacked appearance would
  // get clipped.
  const dragSourceElement = event.currentTarget;
  const sourceBounds = dragSourceElement.getBoundingClientRect();
  const dragPreviewCard = buildDragPreviewCard(
    dragSourceElement,
    procedure.name,
    procedure.templateCollection.name,
    hasMultipleUnitOps,
    classes,
    procedureCardClasses
  );

  // Wrap the lightweight preview with transparent padding so the stacked outer borders are included.
  const dragPreviewWrapper = document.createElement('div');
  dragPreviewWrapper.className = classes.dragPreviewWrapper;
  dragPreviewWrapper.appendChild(dragPreviewCard);
  document.body.appendChild(dragPreviewWrapper);

  const previewOffsetX = event.clientX - sourceBounds.left + DRAG_PREVIEW_STACK_PADDING_PX;
  const previewOffsetY = event.clientY - sourceBounds.top + DRAG_PREVIEW_STACK_PADDING_PX;
  event.dataTransfer.setDragImage(dragPreviewWrapper, previewOffsetX, previewOffsetY);

  const cleanupDragPreview = (): void => {
    dragPreviewWrapper.remove();
  };
  dragSourceElement.addEventListener('dragend', cleanupDragPreview, {once: true});
}

export default function ProcedureSearchSidepanel({
  procedureType,
  studyType,
}: ProcedureSearchSidepanelProps): JSX.Element | null {
  const {classes} = useStyles();
  const {classes: procedureCardClasses} = useProcedureCardStyles();
  const [searchQuery, setSearchQuery] = useState('');
  const procedureString = getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
  });
  const procedureLibraryLabel = `${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.SENTENCE,
    plural: studyType === StudyType.ASSAY_DEVELOPMENT,
  })} library`;
  const searchInputAriaLabel = `Search ${getProcedureUIString(ProceduresStringKeys.PROCEDURE, procedureType, {
    stringCase: StringCase.LOWER,
    plural: true,
  })}`;
  const proceduresResult = useBenchlingQuery<ProcedureSearchSidepanelListingQuery>(
    PROCEDURE_SEARCH_SIDEPANEL_LISTING_QUERY,
    {
      variables: {procedureTypes: [procedureType]},
      renderCachedDataWhenRefetching: true,
    }
  );
  const procedures =
    proceduresResult.type === QueryResultType.SUCCESS ? proceduresResult.data.proceduresListing : [];
  const filteredProcedures = useFilteredProcedures({procedures, searchQuery});

  if (proceduresResult.type === QueryResultType.PENDING) {
    return <CenteredSpinner />;
  }
  if (proceduresResult.type === QueryResultType.ERROR) {
    return null;
  }

  return (
    <Stack spacing={0} className={classes.container}>
      <TextInput
        className={classes.queryInput}
        placeholder="Search"
        aria-label={searchInputAriaLabel}
        value={searchQuery}
        onChange={(event) => {
          setSearchQuery(event.currentTarget.value);
        }}
        {...getTestProps({component: 'ProcedureSearchSidepanel', element: 'queryInput'})}
      />
      <Text className={classes.libraryLabel}>{procedureLibraryLabel}</Text>
      <ScrollArea className={classes.resultsScrollArea} offsetScrollbars={true}>
        <Stack spacing={16} className={classes.resultsList}>
          {filteredProcedures.map((procedure) => {
            const hasMultipleUnitOps = procedure.publishedProcedureFlowchartConfigVersion.nodes.length > 1;
            return (
              <PresentationalProcedureCard
                key={procedure.id}
                procedureId={procedure.id}
                flowchartVersionId={procedure.publishedProcedureFlowchartConfigVersion.id}
                procedureName={procedure.name}
                templateCollectionName={procedure.templateCollection.name}
                procedureType={procedureType}
                studyType={studyType}
                onRemoveProcedure={asyncNoop} // Does not allow removing procedure
                isEditable={false} // Does not allow removing procedure
                hasUpdateAvailable={false} // Does not allow updating procedure
                isUpdateDisabled={false} // Does not allow updating procedure
                onOpenUpdateModal={() => {}}
                hasMultipleUnitOps={hasMultipleUnitOps}
                isDraggable={true}
                onDragStart={(event) => {
                  handleProcedureCardDragStart(
                    event,
                    procedure,
                    hasMultipleUnitOps,
                    classes,
                    procedureCardClasses
                  );
                }}
              />
            );
          })}
          {filteredProcedures.length === 0 && (
            <Text className={classes.emptyResultsText}>{`No ${procedureString}s found.`}</Text>
          )}
        </Stack>
      </ScrollArea>
    </Stack>
  );
}

const useStyles = createStyles((theme) => ({
  container: {
    height: '100%',
    padding: `${theme.spacing.lg} ${theme.spacing.md}`,
  },
  dragPreviewCard: {
    boxSizing: 'border-box',
    backgroundColor: theme.white,
    borderColor: theme.colors.blue[5],
    borderWidth: 1,
    borderStyle: 'solid',
    borderRadius: theme.radius.sm,
    boxShadow: NODE_HOVER_BOX_SHADOW,
  },
  dragPreviewCardStacked: {
    boxShadow: STACKED_NODE_HOVER_BOX_SHADOW,
  },
  dragPreviewContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    padding: 8,
  },
  dragPreviewHeader: {
    display: 'flex',
    alignItems: 'center',
    gap: 8,
  },
  // Offscreen wrapper with transparent padding so setDragImage does not clip the card's box-shadow.
  dragPreviewWrapper: {
    position: 'fixed',
    top: -10000,
    left: -10000,
    pointerEvents: 'none',
    background: 'transparent',
    padding: DRAG_PREVIEW_STACK_PADDING_PX,
  },
  resultsScrollArea: {
    flex: 1,
    minHeight: 0,
  },
  // Keep stacked card borders/shadows from sitting beneath the vertical scrollbar.
  resultsList: {
    paddingRight: theme.spacing.sm,
  },
  queryInput: {
    marginBottom: theme.spacing.lg,
  },
  libraryLabel: {
    color: theme.black,
    fontSize: theme.fontSizes.sm,
    fontWeight: 600,
    lineHeight: '14px',
    marginBottom: theme.spacing.md,
  },
  emptyResultsText: {
    color: theme.colors.gray[6],
    fontSize: theme.fontSizes.sm,
  },
}));

type ProcedureSearchSidepanelClasses = ReturnType<typeof useStyles>['classes'];
